import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  Description,
  Inventory,
  Search,
} from '@mui/icons-material';
import { templateAPI, productAPI, paymentTermAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import useDebounce from '../../utils/useDebounce';

const Templates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    lineItems: [],
    terms: '',
    notes: '',
    validityDays: 30,
    paymentTerms: '',
  });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const [templatesRes, productsRes, paymentTermsRes] = await Promise.all([
        templateAPI.getAll({ search: debouncedSearch }),
        productAPI.getAllForDropdown(),
        paymentTermAPI.getAllForDropdown(),
      ]);
      setTemplates(templatesRes.data.templates);
      setProducts(productsRes.data);
      setPaymentTerms(paymentTermsRes.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSaveTemplate = async () => {
    if (!formData.name) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      if (editingTemplate) {
        await templateAPI.update(editingTemplate._id, formData);
        toast.success('Template updated successfully');
      } else {
        await templateAPI.create(formData);
        toast.success('Template created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await templateAPI.delete(id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      lineItems: template.lineItems || [],
      terms: template.terms || '',
      notes: template.notes || '',
      validityDays: template.validityDays || 30,
      paymentTerms: template.paymentTerms || '',
    });
    setDialogOpen(true);
  };

  const handleUseTemplate = async (template) => {
    try {
      await templateAPI.use(template._id);
      // Navigate to quotation builder with template data
      navigate('/quotations/new', { state: { template } });
    } catch (error) {
      toast.error('Failed to use template');
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      lineItems: [],
      terms: '',
      notes: '',
      validityDays: 30,
      paymentTerms: 'net30',
    });
  };

  const addLineItem = (product) => {
    if (!product) return;
    setFormData({
      ...formData,
      lineItems: [
        ...formData.lineItems,
        { product: product._id, productName: product.name, quantity: 1, discount: 0 }
      ]
    });
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...formData.lineItems];
    updated[index][field] = value;
    setFormData({ ...formData, lineItems: updated });
  };

  const removeLineItem = (index) => {
    setFormData({
      ...formData,
      lineItems: formData.lineItems.filter((_, i) => i !== index)
    });
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)', px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#333', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Quotation Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create reusable templates for faster quotation creation
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => { resetForm(); setDialogOpen(true); }}
          sx={{ 
            backgroundColor: '#875A7B',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            '&:hover': { backgroundColor: '#714B68' },
          }}
        >
          New Template
        </Button>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#999' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
        />
      </Paper>

      {/* Templates Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No templates found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first template to speed up quotation creation
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => setDialogOpen(true)}
            sx={{ 
              backgroundColor: '#875A7B',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#714B68' },
            }}
          >
            Create Template
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template._id}>
              <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light' }}>
                      <Description sx={{ color: 'primary.main' }} />
                    </Box>
                    <Chip
                      label={`Used ${template.usageCount}x`}
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    {template.name}
                  </Typography>
                  {template.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Inventory sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {template.lineItems?.length || 0} products
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Validity: {template.validityDays} days | {template.paymentTerms?.replace('net', 'Net ')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
                  <Box>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(template)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(template._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() => handleUseTemplate(template)}
                  >
                    Use Template
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Template Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Template Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Validity Days"
                      type="number"
                      fullWidth
                      value={formData.validityDays}
                      onChange={(e) => setFormData({ ...formData, validityDays: parseInt(e.target.value) })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Terms</InputLabel>
                      <Select
                        value={formData.paymentTerms}
                        label="Payment Terms"
                        onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                      >
                        {paymentTerms.map((term) => (
                          <MenuItem key={term._id} value={term._id}>
                            {term.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            {/* Products */}
            <Typography variant="subtitle1" fontWeight={600}>Products</Typography>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => `${option.name} - ₹${option.unitPrice?.toLocaleString('en-IN')}`}
              onChange={(e, value) => addLineItem(value)}
              renderInput={(params) => (
                <TextField {...params} label="Add Product" placeholder="Search products..." />
              )}
              value={null}
            />

            {formData.lineItems.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell width={100}>Qty</TableCell>
                      <TableCell width={100}>Discount %</TableCell>
                      <TableCell width={60}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.lineItems.map((item, index) => {
                      const product = products.find(p => p._id === item.product);
                      return (
                        <TableRow key={index}>
                          <TableCell>{item.productName || product?.name}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              inputProps={{ min: 1, style: { textAlign: 'center' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.discount}
                              onChange={(e) => updateLineItem(index, 'discount', parseFloat(e.target.value) || 0)}
                              inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => removeLineItem(index)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TextField
              label="Terms & Conditions"
              fullWidth
              multiline
              rows={3}
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
            />

            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate}>
            {editingTemplate ? 'Update' : 'Create'} Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Templates;
